import { BaseMessageType } from '@repo/shared/dtos/message'
import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import { cn } from '@repo/ui/utils'
import { format } from 'date-fns'
import { ExternalLink } from 'lucide-react'
import React from 'react'
import Markdown from 'react-markdown'
import { LifeCycle, useLifeCycle, useT } from './hooks'

export type MessageProps = Omit<BaseMessageType, 'id'> & {
  className?: string
  footer?: React.ReactNode
  header?: React.ReactNode
  isNewThread: boolean
  onLifeCycle?: LifeCycle
  onAskedForFeedback?: () => void
  displayFeedback?: boolean
}

export default function Message({
  className,
  content,
  createdAtTime,
  footer,
  header,
  isNewThread,
  isSender,
  onLifeCycle,
  onAskedForFeedback,
  displayFeedback,
}: MessageProps) {
  const { t } = useT({ keyPrefix: 'beneficiary-app.feedback' })
  if (isEmptyString(content)) return null
  useLifeCycle(onLifeCycle)
  return (
    <div
      className={cn(
        'my-1 flex flex-col',
        isSender ? 'items-end justify-end' : 'items-start justify-start',
        footer && 'mb-6',
        className,
      )}
    >
      <div
        className={cn(
          'max-w-3/4 relative flex flex-col px-3 py-2',
          isNewThread
            ? isSender
              ? 'rounded-s-lg rounded-br-lg'
              : 'rounded-e-lg rounded-bl-lg'
            : 'rounded-lg',
          isSender ? `bg-primary-100` : `bg-gray-100`,
        )}
      >
        {header}
        <div className={cn('text-base', header && 'pb-1')}>
          <Markdown
            className="custom-markdown-styles"
            components={{
              a: ({ node, ...props }) => (
                <Button variant="outline" size="sm" asChild>
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-fit max-w-fit flex-row flex-wrap items-center justify-between gap-2 text-pretty py-2"
                  >
                    {props.children}
                    <ExternalLink className="size-4" />
                  </a>
                </Button>
              ),
            }}
          >
            {clean(content)}
          </Markdown>
          <span className="select-none opacity-0">:-------</span>
        </div>
        {!isSender && displayFeedback && (
          <div
            className="absolute bottom-2 left-2 cursor-pointer text-xs text-gray-400 hover:underline"
            onClick={onAskedForFeedback}
          >
            {t('report')}
          </div>
        )}
        <div className="absolute bottom-1 right-2">
          <span className="mt-px select-none text-xs font-normal text-gray-400">
            {format(new Date(createdAtTime), 'hh:mma')}
          </span>
        </div>
        {footer}
      </div>
    </div>
  )
}

export const isNewThread = (
  message: BaseMessageType,
  messages: BaseMessageType[],
): boolean => {
  const index = messages.findIndex(({ id }) => id === message.id)
  return !!(messages.at(index - 1)?.isSender !== message.isSender)
}

// FIXME:
function clean(toBeDisplayed: string) {
  if (isEmptyString(toBeDisplayed)) return toBeDisplayed
  let cleanText = toBeDisplayed.trim()
  if (cleanText.endsWith('---')) {
    cleanText = cleanText.slice(0, cleanText.length - 3)
  }
  return cleanText
}
